<template>
  <v-app class="price-app">
    <AppGlobalNotify />
    <template v-if="userData.auth">
      <Sidebar/>

      <v-snackbar
          v-model="isShouldUpdate"
          :timeout="60000"
          location="top"
          color="orange-darken-4"
      >
        ВЫШЛА НОВАЯ ВЕРСИЯ ПРАЙСА
        <template v-slot:actions>
          <v-btn
              color="white"
              variant="outlined"
              size="small"
              @click="reloadThisPage()"
          >
            <b>Обновить</b>
          </v-btn>
        </template>
      </v-snackbar>
    </template>
    <v-main
        v-else
        class="auth-shell d-flex flex-column pa-4"
        style="min-height: 100vh"
    >
      <div class="d-flex justify-end flex-shrink-0">
        <AppThemeToggleBtn />
      </div>
      <div class="d-flex align-center justify-center flex-grow-1">
        <AuthForm />
      </div>
    </v-main>
  </v-app>
</template>

<script lang="ts">
export default { name: 'PriceListApp' };
</script>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useStore } from 'vuex';
import { setAxiosSessionCredentials } from '@/api/axiosSessionAuth';
import Sidebar from '@/components/ui/UiSidebar.vue';
import AuthForm from '@/components/Auth.vue';
import AppGlobalNotify from '@/components/AppGlobalNotify.vue';
import AppThemeToggleBtn from '@/components/ui/AppThemeToggleBtn.vue';
import {
  setAppReleaseUpgradeHandler,
  fetchAndObserveVersionJson,
} from '@/utils/appReleaseWatch';
import { getApiBaseUrl } from '@/config/backendBaseUrl';

const store = useStore();

const updateCheckInterval = ref<ReturnType<typeof setInterval> | null>(null);
const isShouldUpdate = ref(false);

const userData = computed((): Record<string, unknown> => {
  const raw = store.getters['user/userData'];
  if (raw == null || (Array.isArray(raw) && raw.length === 0)) {
    return {};
  }
  return raw as Record<string, unknown>;
});

watch(
  userData,
  (val) => {
    if (val && val.auth && val.id != null) {
      setAxiosSessionCredentials(val.id, (val.hash as string) ?? '');
    } else {
      setAxiosSessionCredentials(null, null);
    }
  },
  { deep: true, immediate: true }
);

function reloadThisPage() {
  window.location.reload();
}

async function pollVersionJson() {
  await fetchAndObserveVersionJson(getApiBaseUrl());
}

function startUpdateChecker() {
  void pollVersionJson();
  updateCheckInterval.value = setInterval(() => {
    void pollVersionJson();
  }, 5 * 60 * 1000);
}

function showUpdateNotification() {
  isShouldUpdate.value = true;
}

setAppReleaseUpgradeHandler(() => {
  showUpdateNotification();
});

onMounted(() => {
  let userDataLS: Record<string, unknown> = {};
  try {
    userDataLS = JSON.parse(localStorage.getItem('userData') || 'null') || {};
  } catch {
    userDataLS = {};
  }
  if (userDataLS.auth) {
    const groups = userDataLS.groups;
    if (Array.isArray(groups)) {
      userDataLS = {
        ...userDataLS,
        groups: groups.map((role: unknown) => parseInt(String(role), 10)),
      };
    }
    store.commit('user/setUserData', userDataLS);
  }
  startUpdateChecker();
});

onBeforeUnmount(() => {
  if (updateCheckInterval.value != null) {
    clearInterval(updateCheckInterval.value);
  }
});
</script>
<style scoped>
.price-app {
  font-family: 'Roboto', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.auth-shell {
  min-height: 100vh;
  background: rgb(var(--v-theme-background));
}
</style>
